import { useCallback, useEffect, useState } from "react";

import { BenchApiError } from "../api/http";
import {
  type Catalog,
  type InvocationInput,
  type InvocationResult,
  fetchCatalog,
  invokeOperation,
} from "../api/catalog";

export type CatalogState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; catalog: Catalog; stale: boolean };

/** What a follow-up check carries into the next operation's form. */
export interface Prefill {
  /** A named example to load first — it supplies the request body, which carry cannot. */
  example: string | null;
  pathParams: Record<string, string>;
  query: Record<string, unknown>;
}

export interface CatalogController {
  state: CatalogState;
  selected: string | null;
  select: (operationId: string) => void;
  /** Open an operation with some fields already answered, from a "Check this" button. */
  selectWithPrefill: (operationId: string, prefill: Prefill) => void;
  /** Consumed once by the form; cleared so a later visit starts clean. */
  prefill: Prefill | null;
  /** What the selected operation's last run sent, so a check can carry a request value forward. */
  lastInput: InvocationInput | null;
  reload: (refresh?: boolean) => Promise<void>;
  run: (input: InvocationInput) => Promise<void>;
  result: InvocationResult | null;
  runError: string | null;
  running: boolean;
}

/**
 * Loads the catalogue for the active session and runs one operation at a time.
 *
 * `sessionKey` changes whenever the environment or identity does, which is what forces a reload:
 * a different deployment can expose a different API surface.
 */
export function useCatalog(sessionKey: string | null): CatalogController {
  const [state, setState] = useState<CatalogState>({ kind: "idle" });
  const [selected, setSelected] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  // Results are kept per operation rather than one at a time.
  //
  // Verification sends a tester back and forth between two operations -- run a push, go and look
  // at the file list, come back. Clearing on navigation threw away the result they were in the
  // middle of checking, so each operation keeps its own last run until it is run again.
  const [runs, setRuns] = useState<Record<string, RunRecord>>({});
  const [running, setRunning] = useState(false);

  const load = useCallback(
    async (refresh = false, signal?: AbortSignal): Promise<void> => {
      setState((current) => (current.kind === "ready" ? current : { kind: "loading" }));
      try {
        const response = await fetchCatalog(refresh, signal);
        if (signal?.aborted) {
          return;
        }
        setState({ kind: "ready", catalog: response.catalog, stale: response.stale });
      } catch (error) {
        if (!signal?.aborted) {
          setState({ kind: "error", message: describe(error) });
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (sessionKey === null) {
      setState({ kind: "idle" });
      setSelected(null);
      // A new session may be a different deployment or identity, so nothing carries over.
      setRuns({});
      return;
    }
    const controller = new AbortController();
    void load(false, controller.signal);
    return () => controller.abort();
  }, [sessionKey, load]);

  const run = useCallback(async (input: InvocationInput): Promise<void> => {
    setRunning(true);
    // A re-run replaces this operation's previous record -- including the request, which a
    // verification check needs because the response alone often does not repeat what was sent.
    setRuns((current) => ({
      ...current,
      [input.operation_id]: { input, result: null, error: null },
    }));
    try {
      // A failed AMS call still resolves: the bench turns it into an explained result rather
      // than an error. Only a bench-side refusal throws.
      const result = await invokeOperation(input);
      setRuns((current) => ({
        ...current,
        [input.operation_id]: { input, result, error: null },
      }));
    } catch (error) {
      setRuns((current) => ({
        ...current,
        [input.operation_id]: { input, result: null, error: describe(error) },
      }));
    } finally {
      setRunning(false);
    }
  }, []);

  const active = selected ? runs[selected] : undefined;

  return {
    state,
    selected,
    select: (operationId) => {
      setSelected(operationId);
      setPrefill(null);
    },
    selectWithPrefill: (operationId, values) => {
      setSelected(operationId);
      setPrefill(values);
    },
    prefill,
    lastInput: active?.input ?? null,
    reload: (refresh = false) => load(refresh),
    run,
    result: active?.result ?? null,
    runError: active?.error ?? null,
    running,
  };
}

/** One operation's last run: what was sent, and what came back. */
interface RunRecord {
  input: InvocationInput;
  result: InvocationResult | null;
  error: string | null;
}

function describe(error: unknown): string {
  return error instanceof BenchApiError ? error.message : `Unexpected failure: ${String(error)}`;
}
